/**
 * Better Auth Client
 *
 * Client-side auth utilities for browser components.
 */

import { createAuthClient } from "better-auth/react";

/**
 * Playwright uses http://127.0.0.1:3000 while NEXT_PUBLIC_APP_URL is often
 * http://localhost:3000. Session cookies are host-specific; auth API calls must
 * use the same origin as the page or getSession() runs cross-origin without cookies.
 */
function resolveAuthClientBaseUrl(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return process.env.NEXT_PUBLIC_APP_URL || "http://localhost:3000";
}

export const authClient = createAuthClient({
  baseURL: resolveAuthClientBaseUrl(),
  fetchOptions: {
    onError(context) {
      const url = context.response?.url ?? "";
      const status = context.response?.status;

      // Session-refresh errors: log but don't throw (Safari WebKit bug)
      if (url.includes("/get-session") || url.includes("/refresh-token")) {
        console.warn("[auth-client] session-refresh error", { url, status });
        return;
      }

      // Report all other auth errors to Sentry
      const errorMessage = `[auth-client] API error: ${status ?? "unknown"} ${url}`;
      console.error(errorMessage);
      import("@/lib/observability/report-client-error")
        .then(({ reportClientError }) => {
          reportClientError(new Error(errorMessage), {
            source: "lib.auth.auth-client",
            feature: "auth",
            action: "fetchOptions.onError",
            extra: { url, status },
          });
        })
        .catch((err) => {
          console.error("[auth-client] failed to report fetch error:", err);
        });
    },
  },
});

// Export typed hooks
export const { signIn, signOut, signUp, useSession } = authClient;
